import type { Node, PageBlueprint, PageGroup } from '@menuvera/engine';

import { createNode } from './newNode';
import { blueprintsForEditor, newNodeId, type TemplateDocument } from './nodeTree';

const GROUPS: PageGroup[] = [
  'cover',
  'toc',
  'divider',
  'category',
  'spotlight',
  'filler',
  'info',
  'backCover',
];

function pageRoot(name: string, children: Node[], background = 'surface'): PageBlueprint['root'] {
  return {
    id: newNodeId('sta'),
    kind: 'stack',
    name,
    direction: 'column',
    gap: '$lg',
    layout: { width: 'full', height: 'full' },
    style: {
      padding: { top: '$xl', right: '$lg', bottom: '$xl', left: '$lg' },
      background,
    },
    children,
  };
}

function txt(
  template: string,
  typography: string,
  extra: Partial<Extract<Node, { kind: 'text' }>> = {},
): Node {
  return {
    ...createNode('text'),
    id: newNodeId('txt'),
    source: { template },
    typography,
    ...extra,
  } as Node;
}

function spacerGrow(): Node {
  return { id: newNodeId('spc'), kind: 'spacer', layout: { grow: 1 } };
}

function spacerSize(size: `$${string}`): Node {
  return { id: newNodeId('spc'), kind: 'spacer', size };
}

function kitBlueprint(group: PageGroup): PageBlueprint {
  switch (group) {
    case 'cover':
      return {
        id: newNodeId('bp'),
        group,
        label: 'Cover',
        variant: 'A',
        constraints: {},
        weight: 10,
        root: pageRoot('Cover', [
          txt('Menu', 'subtitle', { color: 'accent' }),
          spacerGrow(),
          txt('{{restaurant.name}}', 'display', { color: 'onSurface', autoFit: true, maxLines: 3 }),
          txt('{{restaurant.tagline}}', 'itemDesc', { color: 'onSurfaceMuted', maxLines: 3 }),
          {
            ...createNode('shape'),
            id: newNodeId('shp'),
            name: 'Accent bar',
            shape: 'rect',
            fill: 'accent',
            layout: { width: 72, height: 4 },
          } as Node,
          spacerSize('$xl'),
          txt('{{menu.title}}', 'caption', { color: 'onSurfaceMuted' }),
        ]),
      };
    case 'toc':
      return {
        id: newNodeId('bp'),
        group,
        label: 'Contents',
        variant: 'A',
        constraints: {},
        weight: 10,
        root: pageRoot('Contents', [
          txt('Contents', 'display', { color: 'onSurface' }),
          {
            id: newNodeId('rpt'),
            kind: 'repeater',
            source: 'menu.categories',
            as: 'category',
            itemTemplate: {
              id: newNodeId('stk'),
              kind: 'stack',
              direction: 'row',
              gap: '$md',
              align: 'baseline',
              children: [
                txt('{{category.name}}', 'itemName', { layout: { grow: 1 } }),
                txt('{{category.itemCount}}', 'caption', { color: 'onSurfaceMuted' }),
              ],
            },
          },
        ]),
      };
    case 'divider':
      return {
        id: newNodeId('bp'),
        group,
        label: 'Divider',
        variant: 'A',
        constraints: {},
        weight: 8,
        root: pageRoot('Divider', [
          spacerGrow(),
          txt('{{category.name}}', 'display', { color: 'onSurface', align: 'center', autoFit: true, maxLines: 2 }),
          txt('{{category.description}}', 'itemDesc', { color: 'onSurfaceMuted', align: 'center', maxLines: 3 }),
          spacerGrow(),
        ]),
      };
    case 'category':
      return {
        id: newNodeId('bp'),
        group,
        label: 'List',
        variant: 'A',
        constraints: {},
        weight: 10,
        capacity: { min: 1, preferred: 8, max: 12 },
        root: pageRoot('List', [
          txt('{{category.name}}', 'sectionTitle', { color: 'onSurface' }),
          {
            id: newNodeId('rpt'),
            kind: 'repeater',
            source: 'page.items',
            as: 'item',
            itemTemplate: {
              id: newNodeId('stk'),
              kind: 'stack',
              direction: 'row',
              gap: '$md',
              align: 'baseline',
              children: [
                txt('{{item.name}}', 'itemName', { layout: { grow: 1 } }),
                txt('{{item.price}}', 'price', { source: { template: '{{item.price}}', format: 'price' } }),
              ],
            },
          },
        ]),
      };
    case 'spotlight':
      return {
        id: newNodeId('bp'),
        group,
        label: 'Spotlight',
        variant: 'A',
        constraints: {},
        weight: 8,
        root: pageRoot('Spotlight', [
          txt('Featured', 'subtitle', { color: 'accent' }),
          txt('{{item.name}}', 'display', { color: 'onSurface', autoFit: true, maxLines: 3 }),
          txt('{{item.description}}', 'itemDesc', { color: 'onSurfaceMuted', maxLines: 4 }),
          txt('{{item.price}}', 'price', { source: { template: '{{item.price}}', format: 'price' } }),
        ]),
      };
    case 'filler':
      return {
        id: newNodeId('bp'),
        group,
        label: 'Filler',
        variant: 'A',
        constraints: {},
        weight: 6,
        root: pageRoot('Filler', [
          spacerGrow(),
          txt('{{restaurant.tagline}}', 'display', { color: 'onSurface', align: 'center', autoFit: true, maxLines: 5 }),
          spacerGrow(),
        ]),
      };
    case 'info':
      return {
        id: newNodeId('bp'),
        group,
        label: 'Info',
        variant: 'A',
        constraints: {},
        weight: 10,
        root: pageRoot('Info', [
          txt('Visit', 'subtitle', { color: 'accent' }),
          txt('{{restaurant.name}}', 'sectionTitle'),
          txt('{{restaurant.address}}', 'itemDesc', { color: 'onSurfaceMuted' }),
          txt('{{restaurant.phone}}', 'itemName'),
          txt('{{restaurant.hours}}', 'caption', { color: 'onSurfaceMuted' }),
        ]),
      };
    case 'backCover':
      return {
        id: newNodeId('bp'),
        group,
        label: 'Back cover',
        variant: 'A',
        constraints: {},
        weight: 10,
        root: pageRoot(
          'Back',
          [
            spacerGrow(),
            txt('Wi-Fi', 'subtitle', { color: 'accent' }),
            txt('{{restaurant.wifiName}}', 'sectionTitle'),
            txt('Ask our team for the password', 'itemName'),
            txt('{{restaurant.website}}', 'caption', { color: 'onSurfaceMuted' }),
            spacerGrow(),
          ],
          'surfaceAlt',
        ),
      };
  }
}

function isEmptyRoot(node: Node): boolean {
  if (node.kind !== 'stack' && node.kind !== 'group' && node.kind !== 'frame') return false;
  return node.children.length === 0;
}

export function missingBookGroups(
  document: TemplateDocument,
  variantId?: string | null,
): PageGroup[] {
  const blueprints = blueprintsForEditor(document, variantId);
  return GROUPS.filter((group) => {
    const list = blueprints.filter((entry) => entry.group === group);
    if (list.length === 0) return true;
    return list.every((entry) => isEmptyRoot(entry.root));
  });
}

export function fillEmptyBookPages(
  document: TemplateDocument,
  variantId?: string | null,
): boolean {
  const blueprints = blueprintsForEditor(document, variantId);
  let changed = false;
  for (const group of GROUPS) {
    const list = blueprints.filter((entry) => entry.group === group);
    const empty = list.find((entry) => isEmptyRoot(entry.root));
    if (list.length === 0) {
      blueprints.push(kitBlueprint(group));
      changed = true;
      continue;
    }
    if (empty && list.every((entry) => isEmptyRoot(entry.root))) {
      const kit = kitBlueprint(group);
      empty.root = kit.root;
      if (!empty.label) empty.label = kit.label;
      changed = true;
    }
  }
  return changed;
}
